// src/components/MainDomainBookings.js
import React, { useState, useMemo, useCallback } from 'react';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Table,
  Spinner,
  Badge,
  Alert,
  Modal
} from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

const STATUS_VARIANTS = {
  pending: 'warning',
  accepted: 'primary',
  confirmed: 'info',
  completed: 'success',
  cancelled: 'secondary'
};

const toTitleCase = (value = '') =>
  value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB');
};

const formatCurrency = (value) => {
  if (value == null || value === '') return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    const cleaned = Number(String(value).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(cleaned)) return `£${value}`;
    return `£${cleaned.toFixed(2)}`;
  }
  return `£${numeric.toFixed(2)}`;
};

const initialResults = {
  openCount: 0,
  completedCount: 0,
  openBookings: [],
  completedBookings: []
};

const MainDomainBookings = () => {
  const [filters, setFilters] = useState({ phone: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [results, setResults] = useState(initialResults);
  const [lastLookup, setLastLookup] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [receiptForm, setReceiptForm] = useState({ amount: '', email: '', type: 'receipt' });
  const [sendingReceipt, setSendingReceipt] = useState(false);

  const hasSearchCriteria = useMemo(
    () => Boolean(filters.phone.trim() || filters.email.trim()),
    [filters]
  );

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSearch = useCallback(
    async (event) => {
      event.preventDefault();
      const phone = filters.phone.trim();
      const email = filters.email.trim();

      if (!phone && !email) {
        setSearchError('Enter a phone number or email address to search.');
        return;
      }

      setSearchError('');
      setLoading(true);

      try {
        const response = await axios.get('/view-leads/bookings/by-contact', {
          params: { phone, email }
        });

        setResults({
          openCount: response.data.openCount || 0,
          completedCount: response.data.completedCount || 0,
          openBookings: response.data.openBookings || [],
          completedBookings: response.data.completedBookings || []
        });
        setLastLookup(response.data.filtersApplied || { phone: phone || null, email: email || null });
        if ((response.data.openCount || 0) === 0 && (response.data.completedCount || 0) === 0) {
          toast.info('No bookings found for the provided contact details.');
        }
      } catch (error) {
        console.error('Failed to lookup bookings:', error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to lookup bookings. Please try again.';
        setSearchError(message);
        toast.error(message);
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  const handleReset = useCallback(() => {
    setFilters({ phone: '', email: '' });
    setResults(initialResults);
    setSearchError('');
    setLastLookup(null);
  }, []);

  const handleOpenReceiptModal = useCallback((booking) => {
    setSelectedBooking(booking);
    setReceiptForm({
      amount:
        booking?.fareAmountValue != null && Number.isFinite(Number(booking.fareAmountValue))
          ? Number(booking.fareAmountValue).toFixed(2)
          : '',
      email: booking?.customer?.email || '',
      type: 'receipt'
    });
    setShowReceiptModal(true);
  }, []);

  const closeReceiptModal = useCallback(() => {
    setShowReceiptModal(false);
    setSelectedBooking(null);
    setReceiptForm({ amount: '', email: '', type: 'receipt' });
    setSendingReceipt(false);
  }, []);

  const handleReceiptFieldChange = useCallback((event) => {
    const { name, value } = event.target;
    setReceiptForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSendReceipt = useCallback(
    async (event) => {
      event.preventDefault();
      if (!selectedBooking) return;

      const amountValue = parseFloat(receiptForm.amount);
      if (!Number.isFinite(amountValue) || amountValue <= 0) {
        toast.error('Enter a valid fare amount before sending the receipt.');
        return;
      }

      const emailValue = receiptForm.email.trim();
      if (!emailValue) {
        toast.error('Customer email address is required to send a receipt.');
        return;
      }

      setSendingReceipt(true);

      try {
        await axios.post('/view-leads/receipt', {
          jobId: selectedBooking.id,
          amount: amountValue,
          customerEmail: emailValue,
          type: receiptForm.type || 'receipt'
        });

        toast.success('Receipt email has been sent to the customer.');
        closeReceiptModal();
      } catch (error) {
        console.error('Failed to send receipt:', error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          'Failed to send receipt. Please try again.';
        toast.error(message);
        setSendingReceipt(false);
      }
    },
    [closeReceiptModal, receiptForm, selectedBooking]
  );

  const renderBookingsTable = (bookings, options = {}) => {
    if (!bookings.length) {
      return (
        <Alert variant="light" className="mb-0">
          No bookings found.
        </Alert>
      );
    }

    return (
      <div className="table-responsive">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Date &amp; Time</th>
              <th>Journey</th>
              <th>Status</th>
              <th>Customer</th>
              <th>Operator</th>
              <th>Payment</th>
              <th>Fare</th>
              <th>Booking ID</th>
              {options.showActions && <th style={{ width: '160px' }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {bookings.map((booking) => (
              <tr key={booking.id}>
                <td>
                  <div>{formatDateTime(booking.dateTime)}</div>
                  {booking.acceptedAt && (
                    <small className="text-muted">Accepted: {formatDateTime(booking.acceptedAt)}</small>
                  )}
                </td>
                <td>
                  <div>
                    <strong>{booking.from || 'Unknown'}</strong>
                  </div>
                  <div className="text-muted">→ {booking.to || 'Unknown'}</div>
                  <small className="text-muted">
                    Pax: {booking.passengers ?? '—'}
                    {booking.returnTrip && ' · Return trip'}
                    {booking.waitAndReturn && booking.waitHours
                      ? ` · Wait & return (${booking.waitHours}h)`
                      : ''}
                  </small>
                </td>
                <td>
                  <Badge bg={STATUS_VARIANTS[booking.status] || 'secondary'}>
                    {toTitleCase(booking.status || 'unknown')}
                  </Badge>
                  {booking.statusNote && (
                    <div>
                      <small className="text-muted">{booking.statusNote}</small>
                    </div>
                  )}
                </td>
                <td>
                  <div>{booking.customer?.name || '—'}</div>
                  <small className="text-muted d-block">{booking.customer?.phone || '—'}</small>
                  <small className="text-muted d-block">{booking.customer?.email || '—'}</small>
                </td>
                <td>
                  {booking.operator ? (
                    <>
                      <div>
                        <strong>{booking.operator.companyName || 'Unnamed Operator'}</strong>
                      </div>
                      <small className="text-muted d-block">{booking.operator.name || ''}</small>
                      <small className="text-muted d-block">{booking.operator.phoneNumber || 'No phone'}</small>
                      <small className="text-muted d-block">{booking.operator.email || 'No email'}</small>
                    </>
                  ) : (
                    <span className="text-muted">Not yet assigned</span>
                  )}
                </td>
                <td>{booking.paymentMethod || 'Cash'}</td>
                <td>{formatCurrency(booking.fareAmountValue ?? booking.fareAmount)}</td>
                <td>{booking.bookingId || '—'}</td>
                {options.showActions && (
                  <td>
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="w-100"
                      onClick={() => handleOpenReceiptModal(booking)}
                      disabled={!booking.customer?.email}
                    >
                      Email Receipt
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  };

  const lastLookupSummary = useMemo(() => {
    if (!lastLookup) return null;
    const parts = [];
    if (lastLookup.phone) parts.push(`Phone: ${lastLookup.phone}`);
    if (lastLookup.email) parts.push(`Email: ${lastLookup.email}`);
    if (!parts.length) return null;
    return parts.join(' · ');
  }, [lastLookup]);

  return (
    <>
      <Card className="mb-3">
        <Card.Header>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
            <div>
              <h4 className="mb-1">Main Domain Booking Lookup</h4>
              <small className="text-muted">
                Search bookings by customer contact details. Current (open) and completed bookings are listed below.
              </small>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSearch}>
            <Row className="g-3 align-items-end">
              <Col md={6}>
                <Form.Label>Customer Phone</Form.Label>
                <Form.Control
                  name="phone"
                  value={filters.phone}
                  onChange={handleInputChange}
                  placeholder="e.g. 07700 900123"
                />
                <Form.Text className="text-muted">
                  Enter digits only or include spaces. We will match common formats automatically.
                </Form.Text>
              </Col>
              <Col md={6}>
                <Form.Label>Customer Email</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={filters.email}
                  onChange={handleInputChange}
                  placeholder="customer@example.com"
                />
              </Col>
            </Row>
            {searchError && (
              <Alert variant="warning" className="mt-3 mb-0">
                {searchError}
              </Alert>
            )}
            <div className="d-flex justify-content-end gap-2 mt-3">
              <Button
                type="button"
                variant="outline-secondary"
                onClick={handleReset}
                disabled={loading && hasSearchCriteria}
              >
                Clear
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Searching…
                  </>
                ) : (
                  'Search Bookings'
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      {lastLookupSummary && (
        <Alert variant="info" className="d-flex justify-content-between align-items-center">
          <span>
            <i className="bi bi-search me-2" />
            Filters applied: {lastLookupSummary}
          </span>
          <span>
            <Badge bg="primary" className="me-2">
              Open: {results.openCount}
            </Badge>
            <Badge bg="success">Completed: {results.completedCount}</Badge>
          </span>
        </Alert>
      )}

      <Row className="g-3">
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-clock-history me-2" />
                Current Open Bookings
              </span>
              <Badge bg="primary">{results.openCount}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && (
                <div className="py-4 text-center">
                  <Spinner animation="border" />
                </div>
              )}
              {!loading && renderBookingsTable(results.openBookings)}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-check-circle me-2" />
                Completed Bookings
              </span>
              <Badge bg="success">{results.completedCount}</Badge>
            </Card.Header>
            <Card.Body className="p-0">
              {loading && (
                <div className="py-4 text-center">
                  <Spinner animation="border" />
                </div>
              )}
              {!loading && renderBookingsTable(results.completedBookings, { showActions: true })}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showReceiptModal} onHide={closeReceiptModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Email Receipt</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSendReceipt}>
          <Modal.Body>
            {selectedBooking && (
              <>
                <div className="mb-3">
                  <strong>Booking:</strong> {selectedBooking.bookingId || selectedBooking.id}
                  <div className="text-muted small">
                    {formatDate(selectedBooking.dateTime)} · {selectedBooking.from} → {selectedBooking.to}
                  </div>
                </div>
                <Form.Group className="mb-3">
                  <Form.Label>Customer Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={receiptForm.email}
                    onChange={handleReceiptFieldChange}
                    required
                    placeholder="customer@example.com"
                  />
                </Form.Group>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Fare Amount (£)</Form.Label>
                      <Form.Control
                        type="number"
                        name="amount"
                        min="0"
                        step="0.01"
                        value={receiptForm.amount}
                        onChange={handleReceiptFieldChange}
                        placeholder="e.g. 35.00"
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Document Type</Form.Label>
                      <Form.Select name="type" value={receiptForm.type} onChange={handleReceiptFieldChange}>
                        <option value="receipt">Receipt</option>
                        <option value="invoice">Invoice</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeReceiptModal} disabled={sendingReceipt}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={sendingReceipt}>
              {sendingReceipt ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Sending…
                </>
              ) : (
                'Send Receipt'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default MainDomainBookings;




